import type { ReactNode } from 'react';
import { ErrorPanel, SkeletonSet } from '@cwos/ui-admin';
import { usePermissions } from './permissions.js';

/**
 * Route guard for permission-shaped screens (doc 13 T0). While the permission
 * set is resolving, a skeleton stands in; if the principal does not hold the
 * permission, the No-permission screen names the missing permission (doc 01 A7)
 * rather than a bare 403. This is the honest counterpart to hiding the rail item.
 */
export function RequirePermission({
  permission,
  what,
  children,
}: {
  permission: string;
  /**
   * What the screen behind this gate does.
   *
   * Worth saying whenever the permission key does not describe the screen to
   * somebody who has never seen it: naming the key tells a reader what to ask
   * for, but not what they would be asking for. That is the whole of the
   * criterion, and the three callers meet it for three different reasons —
   * `reporting:export` (what is actually in the CSVs), the analytics pair (a
   * key this reader may hold under either of two names, see `GatedAnalytics`),
   * and `intelligence:use` (a surface a reader may never have seen).
   *
   * IT IS NOT ABOUT RARITY, though it was once written that way. This docblock
   * used to say the prop was worth filling in "for a capability granted to NO
   * role by default", which was written for `intelligence:use` when
   * 1757520000000 had seeded it to org_admin alone. 1757630000000 added
   * `supervisor` and 1757640000000 added `worker`, so that key is now held by
   * every shipped global role and the rarity rationale describes none of the
   * callers. The screen-is-unfamiliar rationale describes all three, and does
   * not rot the next time a grant widens.
   */
  what?: string;
  children: ReactNode;
}) {
  const { has, isLoading, isError, refetch } = usePermissions();

  if (isLoading) {
    return <SkeletonSet surface="table" rows={4} />;
  }
  // Unknown is not denied. When the permission set could not be read, the set
  // is empty — and answering that with the No-permission screen accuses the
  // user of lacking access they may well hold, sending them to ask an admin for
  // a permission that is already theirs. Say what actually happened, and offer
  // the retry that fixes it.
  //
  // Only when the answer would otherwise be "no": a cached set from before the
  // failure is still a real answer, and someone holding the permission should
  // keep their screen rather than be interrupted by a refresh that failed.
  if (isError && !has(permission)) {
    return (
      <ErrorPanel
        message="We couldn’t check what you have access to just now. This is a connection problem, not a decision about your account."
        onRetry={refetch}
      />
    );
  }
  if (!has(permission)) {
    return <NoPermission permission={permission} what={what} />;
  }
  return <>{children}</>;
}

export function NoPermission({ permission, what }: { permission: string; what?: string }) {
  return (
    <div className="rn-card" role="status">
      <h2 className="rn-card-title">You don’t have access to this screen</h2>
      {what ? <p className="rn-muted">{what}</p> : null}
      <p className="rn-muted">
        This area needs a permission you don’t currently hold: <code>{permission}</code>. Ask your
        org admin if you need it — access is granted deliberately, never by default.
      </p>
    </div>
  );
}
